import React from 'react';
import { Chip, CSSObject, Typography, SxProps } from '@mui/material';
import styled from '@emotion/styled';
import { convertStylesToStyled } from '@/_utils/utils';
import { selectTitleTypes } from '@/redux/selectors/constantsSelectors';
import { useAppSelector } from '@/redux/hooks';

const TITLE_TYPE_COLORS: Record<string, string> = {
  feature: 'primary.main',
  series: 'secondary.main',
  season: 'tertiary.main',
  episode: 'lavender.main',
  'episode version': 'info.main',
  version: 'info.main',
  trailer: 'teal.main'
};

interface TitleTypeLabelProps {
  type?: string;
  inverted?: boolean;
  width?: string;
  sx?: SxProps;
  style?: CSSObject;
  small?: boolean;
}

const TitleTypeLabel = ({ type, width = '6rem', inverted = false, small = false, sx, style }: TitleTypeLabelProps) => {
  const typeMap: Record<string, string> = useAppSelector(selectTitleTypes);
  if (!type) return null;
  let titleType = typeMap?.[type.toLowerCase()]?.toLowerCase() || type.toLowerCase();
  if (titleType === 'episode version') titleType = 'version';
  const color = TITLE_TYPE_COLORS[titleType as keyof typeof TITLE_TYPE_COLORS];

  if (inverted) {
    return (
      <Typography component="span" sx={{ fontWeight: 800, color, ...sx }}>
        {titleType}
      </Typography>
    );
  }
  return (
    <TitleTypeLabelChip
      type={titleType}
      className={type.toLowerCase()}
      label={titleType}
      small={+small}
      width={width}
      sx={{ ...sx, backgroundColor: color }}
      extra={style}
    />
  );
};

export default TitleTypeLabel;

type TitleStyles = {
  feature: string;
  series: string;
  season: string;
  // special: string;
  episode: string;
  version: string;
};

const TitleTypeLabelChip = styled(Chip)<{
  type: string;
  width: string;
  sx?: SxProps;
  extra?: CSSObject;
  small?: number;
}>`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-right: ${({ theme }) => theme.spacing(1.75)};
  text-transform: uppercase;
  border-style: dotted;
  color: #fff;
  background-color: ${({ theme }) => theme.palette.primary.main};
  font-size: 0.8rem;
  width: ${({ width }) => width};
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.root)}
  ${({ extra }) => convertStylesToStyled(extra)} 
  ${({ small }) =>
    small &&
    `
    font-size: 0.8rem;
    &.MuiChip-root {
      min-height: 0;
      height: auto;
      padding: 4px;
    }
    .MuiChip-label {
      padding: 0;
    }
  `}
  
  .MuiChip-label > span {
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }

  &.MuiChip-root {
    background-color: ${({ theme, type }) => {
      const bgStyles: TitleStyles = {
        feature: theme.palette.primary.main,
        series: theme.palette.secondary.main,
        season: theme.palette.tertiary.main,
        // special: theme.palette.tertiary.main,
        episode: theme.palette.lavender.main,
        version: theme.palette.info.main
      };

      return bgStyles[type as keyof TitleStyles];
    }};
    border-color: ${({ theme, type }) => {
      const borderStyles: TitleStyles = {
        feature: theme.palette.primary.main,
        series: theme.palette.secondary.main,
        season: theme.palette.tertiary.main,
        // special: theme.palette.tertiary.main,
        episode: theme.palette.lavender.main,
        version: theme.palette.info.main
      };
      return borderStyles[type as keyof TitleStyles];
    }};
  }
  // TODO: KYLE feels like this could be variablized so we dont have to update it everytime a new one is added
  &.feature,
  &.movie {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.feature)}
  }

  &.series {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.series)}
  }

  &.season {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.season)}
  }

  &.episode {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.episode)}
  }

  &.version {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.episode)}
  }

  &.trailer {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.trailer)}
  }
  &.segment {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.segment)}
  }
  &.standalone {
    ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.standalone)}
  }
`;
